import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { useFetchRestaurantsSuggestionsQuery } from "../../store";
import { twMerge } from "tailwind-merge";
import { AddressSuggestion, OrderType, Restaurant } from "../../types";
import { FaSearch } from "react-icons/fa";
import Skeleton from "../common/Skeleton";
import { useDebounce } from "../../hooks";

interface SearchBarProps {
  placeholder?: string;
  className?: string;
  orderType: OrderType;
  address: AddressSuggestion;
  onSelect: (restaurant: Restaurant) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({
  placeholder,
  className,
  orderType,
  address,
  onSelect,
}) => {
  // hooks
  const [isOpen, setIsOpen] = useState(false);
  const divEl = useRef<HTMLDivElement>(null);
  const [searchText, setSearchText] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchText, 400);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (!divEl.current) return;
      if (!divEl.current.contains(event.target as Node)) setIsOpen(false);
    };

    document.addEventListener("click", handler, false);

    return () => {
      document.removeEventListener("click", handler);
    };
  }, []);

  useEffect(() => {
    if (debouncedSearchTerm.length > 1) setIsOpen(true);
    else setIsOpen(false);
  }, [debouncedSearchTerm]);

  // queries & mutations
  const { data: restaurants, isFetching: isFetchingRestaurants } =
    useFetchRestaurantsSuggestionsQuery(
      { q: debouncedSearchTerm, orderType, address: address.displayName },
      {
        skip: debouncedSearchTerm.length < 2,
      }
    );

  // events
  const handleOnChange = (e: {
    target: { value: React.SetStateAction<string> };
  }) => {
    setSearchText(e.target.value);
  };

  const handleOptionClick = (option: Restaurant) => {
    setIsOpen(false);
    setSearchText("");
    onSelect(option);
  };

  // classes
  const wrapperClasses = classNames(
    "relative flex flex-col justify-center shadow-lg",
    className
  );

  const inputContainerClasses = twMerge(
    classNames("w-full relative flex flex-col rounded justify-center p-2")
  );
  const inputClasses = classNames("w-full text-gray-700 focus:outline-none");

  // renders
  const renderedOptions = restaurants?.map((restaurant) => {
    return (
      <div
        key={restaurant._id}
        className="hover:bg-gray-200 flex gap-2 rounded cursor-pointer p-1"
        onClick={() => handleOptionClick(restaurant)}
      >
        <div className="flex items-center justify-center rounded-full">
          <img
            src={restaurant.images.logoPath}
            className="h-12 w-16 object-cover rounded-full"
            alt={restaurant.displayName}
          />
        </div>
        <div className="flex flex-col">
          <div>{restaurant.displayName}</div>
          <div className="text-gray-600 text-sm">{restaurant.description}</div>
        </div>
      </div>
    );
  });

  return (
    <div className={wrapperClasses}>
      <div ref={divEl} className={inputContainerClasses}>
        <div className="flex items-center gap-2">
          <FaSearch className="text-primary text-lg" />
          <input
            value={searchText}
            placeholder={placeholder}
            className={inputClasses}
            onChange={handleOnChange}
            autoComplete="off"
          />
        </div>
      </div>

      {isOpen && (
        <div className="absolute top-full w-full border bg-white z-30 overflow-y-auto max-h-60 p-2">
          {isFetchingRestaurants ? (
            <Skeleton className="h-10 w-full" times={3} />
          ) : renderedOptions && renderedOptions.length ? (
            renderedOptions
          ) : (
            <div className="flex flex-col rounded">
              <div>Aucun restaurant trouvé</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
